<!-- Authentication layout (no sidebar/navbar) -->
@if (isAuthPage()) {
  <div class="flex h-screen flex-col">
    <app-update-banner />
    @if (isMockDataActive) {
      <div role="status" aria-label="Mock data warning" class="flex h-7 shrink-0 items-center justify-center gap-2 bg-amber-700 px-4 text-xs font-semibold text-white">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
        </svg>
        <span>MOCK DATA ACTIVE: {{ activeMockServicesDisplay }}</span>
      </div>
    }
    <div class="grow overflow-auto">
      <router-outlet></router-outlet>
    </div>
  </div>
}

<!-- Main application layout - Bloomberg Terminal Pro Style -->
@if (!isAuthPage()) {
  <!-- GAP-FE-037: Skip links for WCAG 2.1 keyboard navigation — only in main layout where targets exist -->
  <app-skip-links></app-skip-links>
  <div class="flex h-screen flex-col">
    <app-update-banner />
    @if (isMockDataActive) {
      <div role="status" aria-label="Mock data warning" class="flex h-7 shrink-0 items-center justify-center gap-2 bg-amber-700 px-4 text-xs font-semibold text-white">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
        </svg>
        <span>MOCK DATA ACTIVE: {{ activeMockServicesDisplay }}</span>
      </div>
    }
    <div class="flex grow overflow-hidden bg-bb-bg-primary text-bb-text-primary">
      <!-- Sidebar -->
      <app-sidebar-modern></app-sidebar-modern>

      <!-- Main content area -->
      <div class="flex grow flex-col overflow-hidden bg-bb-bg-primary">
        <!-- Navbar. F-FU-45: appPublishHeaderBottom keeps --rz-header-bottom on
             :root equal to the navbar's real bottom edge, which moves when the
             update / mock-data banners above it appear or wrap; top toasts use it. -->
        <app-navbar appPublishHeaderBottom></app-navbar>

        <!-- Scrollable main content -->
        <main id="main-content" tabindex="-1" class="grow overflow-auto bg-bb-bg-primary scrollbar-thin scrollbar-track-bb-bg-primary scrollbar-thumb-bb-border hover:scrollbar-thumb-bb-border-strong">
          <!-- F-FU-40: while the floating Copilot launcher can show (its own
               enabled() gate — not "panel closed", so opening the slide-over
               does not change the scroll height), reserve 64px of bottom padding:
               more than the launcher's reach into <main> (≤58px above the footer)
               so scrolled-to-end content and its controls are never under it. -->
          <div
            class="mx-auto max-w-[1920px] px-4 py-4 lg:px-6"
            [class.pb-16]="copilotChat.enabled()"
            data-testid="main-content-inner">
            <router-outlet></router-outlet>
          </div>
        </main>

        <!-- Footer -->
        <app-footer></app-footer>
      </div>
    </div>

    <!-- Interactive copilot slide-over + always-visible launcher. Rendered ONLY
         in the authenticated app shell (never on /auth pages) and self-gates on
         AgentChatService.enabled() (build flag AND runtime status AND the
         permission-only copilot:chat:read authorization) + its own isOpen()
         signal. The panel/launcher are position:fixed, so their placement
         inside this flex container is viewport-relative and unaffected by the
         layout.

         RF-QA-173 · `@defer (when copilotEnabled())` moves the panel component
         (42.3 KiB) plus its generative-UI widget catalogue (10.4 KiB) out of
         the shell's initial chunk. The trigger is the component's own
         `enabled()` gate, so the extra chunk is fetched only for operators the
         copilot is actually lit for; the launcher pill appears the moment that
         gate opens, which already waited on the async /copilot/status response.
         `AgentChatService` itself stays eager — the ⌘J hotkey effect below and
         the palette-entry app-initializer both need it before the panel
         exists. -->
    @defer (when copilotEnabled()) {
      <app-copilot-chat />
    }
  </div>
}

<!-- Global loading spinner -->
@if (loadingService.isLoading$ | async) {
  <app-spinner></app-spinner>
}

<!-- Global toast notifications -->
<ui-toast-container position="top-right"></ui-toast-container>

<!-- GAP-FE-M009 round 6 · Global confirmation dialog. ConfirmationService
     is providedIn: 'root'; this dialog instance subscribes to the root
     service state and renders the modal on every page. Without this any
     `confirmationService.confirm({...})` call would silently set state
     and never present a UI (the accept callback would never fire). -->
<ui-confirm-dialog></ui-confirm-dialog>

<!-- Session timeout warning dialog (DEV-406).
     RF-QA-173 · deferred on the SAME signal that already drove [visible], so
     the dialog renders with visible=true on its very first change detection
     after the chunk resolves — no state is missed. SessionTimeoutService keeps
     running eagerly; only the 6.7 KiB dialog leaves the initial chunk, and it
     is fetched only in sessions that actually go idle.

     `prefetch on idle` matters MORE here than the deferral does: the trigger
     fires after a long idle period, which is exactly when a hashed chunk is
     most likely to be stale (a deploy has landed) or the network has dropped.
     Prefetching during the first idle window puts the chunk in cache while the
     session is healthy. `@error` is the backstop for the case where it still
     fails: a failed @defer fetch is otherwise SILENT, and an invisible timeout
     warning means the operator is logged out with no notice. The fallback is
     plain markup in this template — it loads no chunk of its own and calls the
     same handlers the dialog would. -->
@defer (when sessionTimeoutService.isWarningVisible(); prefetch on idle) {
  <app-session-timeout-dialog
    [visible]="sessionTimeoutService.isWarningVisible()"
    [remainingSeconds]="sessionTimeoutService.remainingSeconds()"
    (extendSession)="onExtendSession()"
    (logout)="onLogout()">
  </app-session-timeout-dialog>
} @error {
  <div
    class="fixed inset-x-0 top-0 z-modal flex items-center justify-center gap-3 bg-amber-700 px-4 py-2 text-sm font-semibold text-white"
    role="alertdialog"
    aria-modal="false"
    aria-label="Session expiring"
    data-testid="session-timeout-fallback">
    <span>Your session expires in {{ sessionTimeoutService.remainingSeconds() }}s.</span>
    <button type="button" class="underline" (click)="onExtendSession()">Stay signed in</button>
    <button type="button" class="underline" (click)="onLogout()">Log out</button>
  </div>
}

<!-- GAP-005 · ⌘K command palette — global; visibility driven by CommandPaletteService -->
<app-command-palette></app-command-palette>

<!-- GAP-FU-10 / FU-12 · Single-instance overlays driven by their respective
     services (SymbolHoverService / GlossaryOverlayService / TourService).
     Mounted once here so every directive across the app shares one DOM tooltip. -->
<app-symbol-hover-card></app-symbol-hover-card>
<app-glossary-overlay></app-glossary-overlay>
<!-- RF-QA-173 · the guided tour is opt-in (⌘K → "Start tour", or the first-run
     prompt), so its overlay is deferred on TourService.activeTour(). The
     service stays eager — ngOnInit's startIfFirstRun() call decides whether a
     tour begins at all, and that decision is what flips this trigger. -->
@defer (when tourActive(); prefetch on idle) {
  <app-tour-overlay></app-tour-overlay>
}

<!-- GAP-FE-001 · Dedicated Symbol Search modal (FU-10). Opens via the
     ⌘⇧S hotkey registered in app.component.ts. Mounted globally so the
     hotkey works from anywhere in the app.
     RF-QA-173 · deferred on the same `symbolSearchOpen()` signal the [open]
     input already reads, so the modal's first render already has open=true.
     The hotkey only sets the signal — it never reaches into the component — so
     nothing observes the modal before the chunk resolves. -->
@defer (when symbolSearchOpen(); prefetch on idle) {
  <app-symbol-search
    [open]="symbolSearchOpen()"
    [symbols]="symbolSearchUniverse()"
    [loading]="symbolSearchLoading()"
    [error]="symbolSearchError()"
    (selected)="onSymbolSelected($event)"
    (retry)="retrySymbolSearch()"
    (closed)="symbolSearchOpen.set(false)">
  </app-symbol-search>
}

<!-- GAP-FE-001 · Replay scrubber bar (FU-11). Driven entirely by
     ReplayService; opt-in — hidden until the operator explicitly enters replay
     mode (⌘K → "Enter replay mode"), and the bar's ✕ exits back to live. The
     old `maxMs > minMs` gate was effectively always-true (maxMs is always now,
     minMs falls back to now−window on an empty buffer), so the bar showed
     unconditionally.
     RF-QA-173 · the `@if` STAYS and the size win comes from a nested
     `@defer (on immediate)`. `@defer (when …)` alone would have been a bug:
     ɵɵdeferWhen only acts while the block is still in its Initial/Placeholder
     state, so a falsy predicate AFTER the block has rendered is a no-op and the
     block never unmounts. The four other deferred blocks in this file survive
     that because each deferred component self-gates its own root on the very
     signal that triggers it; ReplayBarComponent does NOT — its root div is
     ungated and reads `isReplaying()`, which ReplayService documents as
     distinct from `active()`. A one-way defer would therefore pin this sticky
     bottom bar over every route for the rest of the session and make its ✕
     inert. The `@if` keeps destruction working; the nested defer keeps the bar
     out of the initial chunk until replay mode is first entered. -->
@if (replayService.active()) {
  @defer (on immediate) {
    <app-replay-bar></app-replay-bar>
  }
}
